import type { User } from "../../../services/api";
import { stripLeadIn } from "../leadInWords";
import { askFor, findByName, findNamedIn } from "./findByName";
import type { SlotName } from "./types";
import { noneCalled } from "./wording";

/** The name a message gives: a short reply as it is, otherwise its capitalised words after the first. */
function nameSaid(said: string, notNames: RegExp): string | null {
  const stripped = stripLeadIn(said.replace(/['’]s\b/g, "").replace(/[?.!]+$/, ""));
  if (!stripped) return null;
  const words = stripped.split(/\s+/);
  if (words.length <= 3) return words.join(" ");
  const capitalised = words.slice(1).filter((w) => /^[A-Z]/.test(w) && w !== "I" && !notNames.test(w));
  return capitalised.length > 0 ? capitalised.join(" ") : null;
}

/**
 * The person a message names, by whole name or first name ("Jane Dube", "Jane"),
 * or a short reply that is part of a name. `question` asks again, offering `choices`.
 * `kind` is what they're called in the reply ("User", "team member"), and `slot` the detail it asks for.
 */
export function pickNamed<T>(
  people: T[],
  nameOf: (person: T) => string,
  said: string,
  question: string,
  { slot, kind, notNames }: { slot: SlotName; kind: string; notNames: RegExp },
  choices: T[] = people
) {
  const named = people.filter((p) => nameOf(p)?.trim());
  const firstName = (p: T) => nameOf(p).trim().split(/\s+/)[0] ?? "";
  const byFullName = findNamedIn(named, said, nameOf);
  const byFirstName = byFullName.length > 0 ? byFullName : findNamedIn(named, said, firstName);
  const wanted = nameSaid(said, notNames);
  const found = byFirstName.length > 0 ? byFirstName : wanted ? findByName(named, wanted, nameOf) : [];
  if (found.length === 1) return { person: found[0] };
  if (found.length > 1) return askFor(slot, "ambiguous", `Which ${kind} do you mean?`, found.map(nameOf));
  const offered = choices.map(nameOf).filter(Boolean);
  return wanted
    ? askFor(slot, "notFound", noneCalled(kind, wanted, offered), offered)
    : askFor(slot, "missing", question, offered);
}

/** The User a message names. */
export function pickUser(users: User[], said: string, question: string, choices: User[] = users) {
  const pick = pickNamed(users, (u) => u.name, said, question, { slot: "user", kind: "User", notNames: /^Circles?$/ }, choices);
  return "person" in pick ? { user: pick.person } : pick;
}
